import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  CircleAlert,
  Eye,
  LockKeyhole,
} from "lucide-react";
import { metricDisplay } from "../format";
import type {
  EvidenceStatus,
  GameAsset,
  MetricCatalogEntry,
  MetricObservation,
  Reliability,
} from "../types";
import { InfoTip } from "./InfoTip";

const TITLE_CASE_EXCEPTIONS = new Set(["a", "an", "and", "as", "at", "but", "by", "for", "from", "in", "into", "nor", "of", "on", "or", "over", "per", "so", "the", "to", "up", "via", "vs", "vs.", "with", "yet"]);

function titleCase(value: string) {
  return value.split(/(\s+)/).map((part, index, parts) => {
    if (/^\s+$/.test(part)) return part;
    const stripped = part.replace(/^[^\p{L}\p{N}]*/u, "").replace(/[^\p{L}\p{N}]+$/u, "");
    const isFirstOrLast = index === 0 || index === parts.length - 1;
    if (!stripped || (!isFirstOrLast && TITLE_CASE_EXCEPTIONS.has(stripped.toLowerCase())) || stripped === stripped.toUpperCase()) return part;
    return part.replace(stripped, stripped.split("-").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join("-"));
  }).join("");
}

export function GameMark({ title, asset, size = "md" }: { title: string; asset?: GameAsset; size?: "sm" | "md" | "lg" }) {
  const initials = title
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return asset?.url ? (
    <img className={`game-mark game-mark--${size}`} src={asset.url} alt={asset.alt} loading="lazy" />
  ) : (
    <span
      className={`game-mark game-mark--${size} game-mark--fallback`}
      style={{ "--game-accent": asset?.accent ?? "#6f7c98" } as React.CSSProperties}
      role="img"
      aria-label={`${title} artwork unavailable; showing monogram`}
    >
      {initials}
    </span>
  );
}

export function EvidenceBadge({ reliability }: { reliability: Reliability }) {
  const label: Record<Reliability, string> = {
    publishable_high_confidence: "High confidence",
    publishable_directional: "Directional",
    exploratory: "Exploratory",
    insufficient_evidence: "Not sufficient",
  };
  return <span className={`evidence-badge evidence-badge--${reliability}`}>{label[reliability]}</span>;
}

export function StatusBadge({ status }: { status: EvidenceStatus }) {
  const supported = status === "supported";
  const labels: Record<EvidenceStatus, string> = {
    supported: "Supported",
    short_horizon: "Early signal",
    insufficient_evidence: "Evidence not yet sufficient",
    privacy_suppressed: "Privacy protected",
    disabled: "Not included",
  };
  return (
    <span className={`status-badge status-badge--${status}`}>
      {supported ? <CheckCircle2 size={14} aria-hidden="true" /> : <CircleAlert size={14} aria-hidden="true" />}
      {labels[status]}
    </span>
  );
}

function DeltaIcon({ direction }: { direction?: MetricObservation["direction"] }) {
  if (direction === "up") return <ArrowUp size={14} strokeWidth={2.5} aria-hidden="true" />;
  if (direction === "down") return <ArrowDown size={14} strokeWidth={2.5} aria-hidden="true" />;
  return <ArrowRight size={14} strokeWidth={2.5} aria-hidden="true" />;
}

export function MetricCard({
  metric,
  catalog,
  featured = false,
}: {
  metric: MetricObservation;
  catalog?: MetricCatalogEntry;
  featured?: boolean;
}) {
  const deltaText = metric.delta_display ?? (metric.delta === null || metric.delta === undefined
    ? "No prior comparison"
    : catalog?.unit === "percent"
      ? `${metric.delta > 0 ? "+" : ""}${metric.delta.toFixed(1)} pts`
      : `${metric.delta > 0 ? "+" : ""}${Math.round(metric.delta)}`);
  return (
    <article className={`metric-card ${featured ? "metric-card--featured" : ""}`}>
      <div className="metric-card__label">
        <span>{metric.label}</span>
        <InfoTip entry={catalog} label={metric.label} />
      </div>
      <div className="metric-card__value">{metricDisplay(metric, metric.unit === "count")}</div>
      <div className={`metric-delta metric-delta--${metric.valence ?? "neutral"}`}>
        <DeltaIcon direction={metric.direction} />
        <span>{deltaText}</span>
        <span className="metric-delta__context">{metric.delta_label ?? "vs prior wave"}</span>
      </div>
      <div className="metric-card__meta">
        <span>{metric.maturity}</span>
        <EvidenceBadge reliability={metric.reliability} />
      </div>
    </article>
  );
}

export function SectionHeading({
  number,
  eyebrow,
  headline,
  takeaway,
}: {
  number: string;
  eyebrow: string;
  headline: string;
  takeaway: string;
}) {
  return (
    <header className="section-heading">
      <div className="section-heading__eyebrow"><span>{number}</span>{titleCase(eyebrow)}</div>
      <h2>{titleCase(headline)}</h2>
      <p>{takeaway}</p>
    </header>
  );
}

export function EvidenceCallout({
  status,
  title,
  children,
}: {
  status: EvidenceStatus;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <aside className={`evidence-callout evidence-callout--${status}`}>
      <div className="evidence-callout__icon">
        {status === "supported" ? <Eye aria-hidden="true" /> : <LockKeyhole aria-hidden="true" />}
      </div>
      <div>
        <StatusBadge status={status} />
        <h3>{title}</h3>
        <p>{children}</p>
      </div>
    </aside>
  );
}

export function ChartData({ children, label = "View Chart Data", open, onToggle }: { children: React.ReactNode; label?: string; open?: boolean; onToggle?: (open: boolean) => void }) {
  return (
    <details className="chart-data" open={open} onToggle={(event) => onToggle?.(event.currentTarget.open)}>
      <summary>{titleCase(label)}</summary>
      <div className="chart-data__inner">{children}</div>
    </details>
  );
}

export function WhatItMeans({ children }: { children: React.ReactNode }) {
  return (
    <aside className="what-it-means">
      <span>What This Means</span>
      <p>{children}</p>
    </aside>
  );
}
